import { FrameName } from "@/game/entities/config";
import type { TileCoordinates } from "@/types/tile";
import { QuickToolActions } from "@/types/upgrades";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";

type QuickToolStore = {
	active: ComputedRef<boolean>;
	show: ComputedRef<boolean>;
	building: ComputedRef<FrameName>;
	error: ComputedRef<boolean>;
	action: ComputedRef<QuickToolActions>;
	highwayStartCoordinates: ComputedRef<TileCoordinates | null>;
	setActive(active: boolean): void;
	setShow(show: boolean): void;
	setBuilding(building: FrameName): void;
	setError(upgradeBought: boolean): void;
	setAction(action: QuickToolActions): void;
	setHighwayStartCoordinates(coordinates: TileCoordinates | null): void;
};

export type QuickToolStoreState = {
	active: boolean;
	show: boolean;
	building: FrameName;
	error: boolean;
	action: QuickToolActions;
	highwayStartCoordinates: TileCoordinates | null;
};

const STORE_NAME = "quick-tool";

export default defineStore(STORE_NAME, (): QuickToolStore => {
	const state = createImmutableState<QuickToolStoreState>({
		active: false,
		show: false,
		building: FrameName.NOT_SET,
		error: false,
		action: QuickToolActions.NONE,
		highwayStartCoordinates: null,
	});

	const active = getImmutableStateProperty(state, "active", false);
	const show = getImmutableStateProperty(state, "show", false);
	const building = getImmutableStateProperty(
		state,
		"building",
		FrameName.NOT_SET,
	);
	const error = getImmutableStateProperty(state, "error", false);
	const action = getImmutableStateProperty(
		state,
		"action",
		QuickToolActions.NONE,
	);
	const highwayStartCoordinates = getImmutableStateProperty(
		state,
		"highwayStartCoordinates",
		null,
	);

	const setActive = (active: boolean): void =>
		setImmutableStateProperty(state, "active", active);

	const setShow = (show: boolean): void =>
		setImmutableStateProperty(state, "show", show);

	const setBuilding = (building: FrameName): void =>
		setImmutableStateProperty(state, "building", building);

	const setError = (error: boolean): void =>
		setImmutableStateProperty(state, "error", error);

	const setAction = (action: QuickToolActions): void =>
		setImmutableStateProperty(state, "action", action);

	const setHighwayStartCoordinates = (
		coordinates: TileCoordinates | null,
	): void =>
		setImmutableStateProperty(
			state,
			"highwayStartCoordinates",
			coordinates,
		);

	return {
		active,
		show,
		building,
		error,
		action,
		highwayStartCoordinates,
		setActive,
		setShow,
		setBuilding,
		setError,
		setAction,
		setHighwayStartCoordinates,
	};
});
